import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import { useAuth } from "../context/AuthContext";

type HardwareCapability = {
  codec: string;
  methods: string[];
};

type HardwareCapabilities = {
  decoding: HardwareCapability[];
  encoding: HardwareCapability[];
  flywheel: {
    available: boolean;
    encoder?: string;
    reason?: string;
  };
};

type CapabilitiesDialogProps = {
  open: boolean;
  onClose: () => void;
};

const CapabilityList = ({
  title,
  items,
  emptyLabel
}: {
  title: string;
  items: HardwareCapability[];
  emptyLabel: string;
}) => (
  <Stack spacing={1.5}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    ) : (
      items.map((item) => {
        const methods = item.methods ?? [];
        return (
          <Box
            key={`${title}-${item.codec}`}
            sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 56, textTransform: "uppercase" }}>
              {item.codec}
            </Typography>
            {methods.length > 0 ? (
              methods.map((method) => <Chip key={method} size="small" label={method} variant="outlined" />)
            ) : (
              <Typography variant="body2" color="text.secondary">
                None detected
              </Typography>
            )}
          </Box>
        );
      })
    )}
  </Stack>
);

const CapabilitiesDialog = ({ open, onClose }: CapabilitiesDialogProps) => {
  const { authorizedFetch } = useAuth();
  const [capabilities, setCapabilities] = useState<HardwareCapabilities | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchCapabilities = useCallback(
    async (isActive: () => boolean) => {
      setLoading(true);
      setError(null);
      try {
        const response = await authorizedFetch("/api/server/capabilities");
        const payload = (await response.json().catch(() => null)) as
          | (Partial<HardwareCapabilities> & { error?: string })
          | null;

        if (!response.ok) {
          throw new Error(
            payload && typeof payload.error === "string" ? payload.error : "Unable to load hardware capabilities"
          );
        }

        if (!isActive()) {
          return;
        }

        setCapabilities({
          decoding: (payload?.decoding ?? []).map((cap) => ({
            codec: cap.codec,
            methods: cap.methods ?? []
          })),
          encoding: (payload?.encoding ?? []).map((cap) => ({
            codec: cap.codec,
            methods: cap.methods ?? []
          })),
          flywheel: {
            available: payload?.flywheel?.available === true,
            encoder: payload?.flywheel?.encoder,
            reason: payload?.flywheel?.reason
          }
        });
      } catch (err) {
        if (!isActive()) {
          return;
        }
        setCapabilities(null);
        setError(err instanceof Error ? err.message : "Unable to load hardware capabilities");
      } finally {
        if (isActive()) {
          setLoading(false);
        }
      }
    },
    [authorizedFetch]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    let active = true;

    void fetchCapabilities(() => active);

    return () => {
      active = false;
    };
  }, [fetchCapabilities, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>⚙️ Capabilities</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Registered hardware methods are grouped by codec. Flywheel is verified separately with its complete compositor-to-encoder path.
            </Typography>
            {capabilities?.flywheel.available ? (
              <Alert severity="success">
                Flywheel hardware path is operational{capabilities.flywheel.encoder ? ` with ${capabilities.flywheel.encoder}` : ""}.
              </Alert>
            ) : (
              <Alert severity="warning">
                Flywheel hardware path is unavailable: {capabilities?.flywheel.reason || "the operational probe did not complete successfully"}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CapabilityList
                  title="Decoding"
                  items={capabilities?.decoding ?? []}
                  emptyLabel="No hardware decoding capabilities detected."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CapabilityList
                  title="Encoding"
                  items={capabilities?.encoding ?? []}
                  emptyLabel="No hardware encoding capabilities detected."
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CapabilitiesDialog;
